import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import max from 'lodash-es/max'

import { PlayerProfileResponse } from 'protos'
import { playerProfilePalette } from 'lib'

import flatMapTimelineSegments from './flatMapLineSegments'
import { getTimelineEventColor } from '../../lib'

type Props = {
  zoom: number
  width: number
  liveIndex: number
  primaryTimeline: PlayerProfileResponse.TimelinePoint[]
  secondaryTimeline: PlayerProfileResponse.TimelinePoint[]
}

const TimelineChart: FC<Props> = ({
  zoom,
  width,
  liveIndex,
  primaryTimeline,
  secondaryTimeline,
}) => {
  const { formatMessage } = useIntl()

  const highestScore = max([
    ...primaryTimeline.map(({ yScore }) => yScore),
    ...secondaryTimeline.map(({ yScore }) => yScore),
  ])!

  const shouldDisplayLabels = zoom >= 0.8

  const padding = {
    top: 30,
    right: 50,
    bottom: shouldDisplayLabels ? 100 : 5,
    left: 30,
  }

  const minXScale = (width - (padding.left + padding.right)) / (primaryTimeline.length - 1)
  const maxXScale = 32

  const graphHeight = 200 - 2 * padding.top

  const scaleX = minXScale + zoom * (maxXScale - minXScale)
  const graphWidth = scaleX * (primaryTimeline.length - 1)

  const scaleY = (graphHeight - 15) / highestScore

  const calculateXCoord = (index: number) => padding.left + index * scaleX
  const calculateYCoord = (score: number) => padding.top + graphHeight - (scaleY * score)

  const renderLabels = () => {
    return primaryTimeline
      .filter((_, i) => (
        (zoom >= 0.8) ||
        (zoom >= 0.4 && i % 2 === 0) ||
        (zoom >= 0.2 && i % 4 === 0) ||
        (zoom >= 0.02 && i % 8 === 0) ||
        (i % 16 === 0))
      )
      .map((curr, i) => (
        <text
          key={`text-${i}`}
          x={calculateXCoord(curr.xIndex)}
          y={padding.top}
          opacity={.3}
          fill="#FFFFFF"
          fontSize="9"
          fontWeight="500"
          textAnchor="middle"
        >
          {curr.xLabel.replace(/\s/, '')}
        </text>
      ))
  }

  const renderSecondaryScoreLine = () => {
    return flatMapTimelineSegments(secondaryTimeline, ({ prev, curr }, i) => {
      const x1 = calculateXCoord(prev.xIndex)
      const y1 = calculateYCoord(prev.yScore)
      const x2 = calculateXCoord(curr.xIndex)
      const y2 = calculateYCoord(curr.yScore)

      return [
        <line
          key={`secondary-line-${i}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#FFFFFF"
          strokeWidth={2}
          opacity={curr.projection ? 0.1 : 0.3}
        />,
      ]
    })
  }

  const renderPrimaryScoreLineWithEvents = () => {
    return flatMapTimelineSegments(primaryTimeline, ({ prev, curr }, i) => {
      const x1 = calculateXCoord(prev.xIndex)
      const y1 = calculateYCoord(prev.yScore)
      const x2 = calculateXCoord(curr.xIndex)
      const y2 = calculateYCoord(curr.yScore)

      const elements = [] as JSX.Element[]

      elements.push(
        <line
          key={`primary-line-${i}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={playerProfilePalette.playerPerformance[0]}
          strokeWidth={2}
          opacity={curr.projection ? 0.3 : 1}
        />
      )

      const yBase = padding.top + graphHeight

      ;(curr.event as PlayerProfileResponse.TimelineEvent[]).forEach((event, j) => {
        if (event.teamSubIn || event.teamSubOut) {
          return
        }

        const color = getTimelineEventColor(event)

        elements.push(
          <circle
            key={`circle-${i}-${j}`}
            cx={x2 - j * 14}
            cy={yBase + 20}
            r="5"
            fill={color}
          />
        )

        if (shouldDisplayLabels) {
          elements.push(
            <text
              key={`text-${i}-${j}`}
              x={x2 - (j * 14) - 3}
              y={yBase + 20 + 10}
              fill={color}
              fontSize="9"
              fontWeight="500"
              transform={`rotate(90,${x2 - (j * 14) - 3},${yBase + 20 + 10})`}
            >
              {event.shortTitle}
            </text>
          )
        }
      })

      elements.push(
        <path
          key={`area-${i}`}
          d={`M${x1} ${yBase} L${x1} ${y1} L${x2} ${y2} L${x2} ${yBase} Z`}
          fill={!curr.notInTeam ? playerProfilePalette.playerPerformance[0] : '#FFFFFF'}
          opacity={.1}
        />
      )

      return elements
    })
  }

  const renderLiveMinuteIndicator = () => {
    return liveIndex >= 0 && (
      <>
        <line
          x1={calculateXCoord(liveIndex)}
          y1={0}
          x2={calculateXCoord(liveIndex)}
          y2={padding.top + graphHeight + padding.bottom}
          stroke="#904650"
          strokeWidth={2}
          strokeDasharray="4 1"
        />

        <rect
          x={calculateXCoord(liveIndex)}
          y={16}
          rx={2}
          width={40}
          height={20}
          fill="#FF5C5C"
        />

        <text
          x={calculateXCoord(liveIndex) + 20}
          y={16 + 14}
          fill="#FFFFFF"
          fontSize="12"
          fontWeight="500"
          textAnchor="middle"
        >
          {formatMessage({ id: 'playerprofile_timeline_live_tag' })}
        </text>
      </>
    )
  }

  const renderScore = (timeline: PlayerProfileResponse.TimelinePoint[], color: string) => {
    const lastPoint = timeline[timeline.length - 1]
    const latestXIndex = lastPoint.xIndex
    const latestYScore = lastPoint.yScore

    return (
      <text
        x={calculateXCoord(latestXIndex) + 10}
        y={calculateYCoord(latestYScore) + 5}
        fill={color}
        fontSize="14"
        fontWeight="700"
      >
        {latestYScore}
      </text>
    )
  }

  return (
    <svg
      style={{
        width: padding.left + graphWidth + padding.right,
        height: padding.top + graphHeight + padding.bottom,
      }}
    >
      {renderLabels()}
      {renderSecondaryScoreLine()}
      {renderScore(secondaryTimeline, '#FFFFFF32')}
      {renderPrimaryScoreLineWithEvents()}
      {renderScore(primaryTimeline, playerProfilePalette.playerPerformance[0])}
      {renderLiveMinuteIndicator()}
    </svg>
  )
}

export default TimelineChart
try {
    // @ts-ignore
    TimelineChart.displayName = "TimelineChart";
    // @ts-ignore
    TimelineChart.__docgenInfo = { "description": "", "displayName": "TimelineChart", "props": { "zoom": { "defaultValue": null, "description": "", "name": "zoom", "required": true, "type": { "name": "number" } }, "width": { "defaultValue": null, "description": "", "name": "width", "required": true, "type": { "name": "number" } }, "liveIndex": { "defaultValue": null, "description": "", "name": "liveIndex", "required": true, "type": { "name": "number" } }, "primaryTimeline": { "defaultValue": null, "description": "", "name": "primaryTimeline", "required": true, "type": { "name": "any[]" } }, "secondaryTimeline": { "defaultValue": null, "description": "", "name": "secondaryTimeline", "required": true, "type": { "name": "any[]" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/PlayerLayer/components/PlayerProfile/tabs/TimelineTab/components/TimelineChart/TimelineChart.tsx#TimelineChart"] = { docgenInfo: TimelineChart.__docgenInfo, name: "TimelineChart", path: "src/components/PlayerLayer/components/PlayerProfile/tabs/TimelineTab/components/TimelineChart/TimelineChart.tsx#TimelineChart" };
}
catch (__react_docgen_typescript_loader_error) { }